import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '..'; // Apollo Client instance
import { CREATE_DATA_BUCKET, UPDATE_DATA_BUCKET, DELETE_DATA_BUCKET, FULL_DATA_BUCKET_TREE, GET_FIELDS } from '../Queries/options'; // GraphQL queries and mutations
import { setLoader } from './loaderSlice';


function updateNodeById(nodes, id, updater) {
  return nodes.map(node => {
    if (node._id === id) {
      return updater(node);
    }
    if (node.children && node.children.length > 0) {
      return { ...node, children: updateNodeById(node.children, id, updater) };
    }
    return node;
  });
}

// Helper to recursively delete a node by id
function deleteNodeById(nodes, id) {
  return nodes
    .filter(node => node._id !== id)
    .map(node => ({
      ...node,
      children: node.children ? deleteNodeById(node.children, id) : [],
    }));
}

// Helper to recursively add a child to a parent node by id
function addNodeToParent(nodes, parentId, newNode) {
  return nodes.map(node => {
    if (node._id === parentId) {
      return {
        ...node,
        children: [...(node.children || []), newNode],
      };
    }
    if (node.children && node.children.length > 0) {
      return { ...node, children: addNodeToParent(node.children, parentId, newNode) };
    }
    return node;
  });
}
// Fetch Nested Data (Tree structure)
export const fetchNestedData = createAsyncThunk(
  'dataBucket/fetchNestedData',
  async ({ silent = false } = {}, { rejectWithValue,dispatch }) => {
    try {
      if (!silent) dispatch(setLoader(true));
      const { data } = await client.query({
        query: FULL_DATA_BUCKET_TREE,
      });

      // Parse the JSON string returned from GraphQL
      const parsedData = JSON.parse(data.fullDataBucketTree);
      return parsedData;
    } catch (error) {
      return rejectWithValue(error.message);
    }
    finally{
      dispatch(setLoader(false));
    }
  }
);

// Fetch Dynamic Fields (Other data related to the buckets)
export const fetchData = createAsyncThunk(
  'dataBucket/fetchData',
  async (skip, {dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoader(true));
      const { data } = await client.query({
        skip,
        query: GET_FIELDS,
      });
      
      return data.GetDynamicField; // Return the fetched data
    } catch (error) {
      return rejectWithValue(error.message);
    }
    finally{
      dispatch(setLoader(false));
    }
  }
);

// Create Data Bucket
export const createDataBucket = createAsyncThunk(
  'dataBucket/createDataBucket',
  async ({ name, bucketLevel, parentId }, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: CREATE_DATA_BUCKET,
        variables: { name, bucketLevel, parentId },
      });
      return data.createDataBucket; // Return the newly created bucket
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update Data Bucket
export const updateDataBucket = createAsyncThunk(
  'dataBucket/updateDataBucket',
  async ({ id, name, bucketLevel, parentId }, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_DATA_BUCKET,
        variables: { id, name, bucketLevel, parentId },
      });
      return data.updateDataBucket; // Return the updated bucket
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Delete Data Bucket
export const deleteDataBucket = createAsyncThunk(
  'dataBucket/deleteDataBucket',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: DELETE_DATA_BUCKET,
        variables: { id },
      });
      return id; // Return the ID of the deleted bucket
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  databucket: [],
  NestedDatabucket: [],
  loading: false,
  error: null,
};

// Slice
const DataBucket = createSlice({
  name: 'DataBucket',
  initialState,
  reducers: {
    addDataBucketItem(state, action) {
      const { parentId, newItem } = action.payload;
      if (!parentId) {
        // Add as a new page
        state.NestedDatabucket.push({ ...newItem, children: [] });
      } else {
        state.NestedDatabucket = addNodeToParent(state.NestedDatabucket, parentId, { ...newItem, children: [] });
      }
    },
    // Update Option/Page/Dropdown
    updateDataBucketItem(state, action) {
      const { id, updatedData } = action.payload;
      state.NestedDatabucket = updateNodeById(state.NestedDatabucket, id, node => ({
        ...node,
        ...updatedData,
      }));
    },
    // Delete Option/Page/Dropdown
    deleteDataBucketItem(state, action) {
      const id = action.payload;
      state.NestedDatabucket = deleteNodeById(state.NestedDatabucket, id);
    },
    // Handle adding the new bucket to the state
    setDataBucket(state, action) {
      state.databucket = action.payload;
    },
    // Handle adding a new bucket created via API
    setNewObj(state, action) {
      const newData = action.payload?.data?.CreateDynamicField?.response;
      state.databucket = [newData, ...state.databucket];
    },
    // Handle updating an existing bucket in the state
    updateField(state, action) {
      const { id, objId, newData } = action.payload;
      if (objId) {
        const field = state.databucket.find(item => item._id === id);
        if (field) {
          const element = field.data.find(item => item._id === objId);
          if (element) {
            element.status = newData.status;
            element.name = newData.name;
          }
        }
      } else {
        const field = state.databucket.find(item => item._id === id);
        if (field) {
          field.data.push({ ...newData, status: true });
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch data (Dynamic Fields)
      .addCase(fetchData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchData.fulfilled, (state, action) => {
        state.loading = false;
        state.databucket = action.payload;
      })
      .addCase(fetchData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch nested data (Tree structure)
      .addCase(fetchNestedData.fulfilled, (state, action) => {
        state.loading = false;
        state.NestedDatabucket = action.payload;
      })
      
      // Handle create bucket (Add the new bucket to the state)
      // .addCase(createDataBucket.fulfilled, (state, action) => {
      //   state.loading = false;
      //   state.NestedDatabucket = [...state.NestedDatabucket, action.payload];
      // })
      
      // Handle update bucket (Update the bucket in the state)
      .addCase(updateDataBucket.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.NestedDatabucket.findIndex(
          (item) => item._id === action.payload._id
        );
        if (index !== -1) {
          state.NestedDatabucket[index] = action.payload;
        }
      })
      
      // Handle delete bucket (Remove the deleted bucket from the state)
      .addCase(deleteDataBucket.fulfilled, (state, action) => {
        state.loading = false;
        state.NestedDatabucket = state.NestedDatabucket.filter(
          (item) => item._id !== action.payload
        );
      });
  },
});

export const { setDataBucket, setNewObj, updateField,addDataBucketItem,updateDataBucketItem,deleteDataBucketItem,} = DataBucket.actions;
export default DataBucket.reducer;
